import * as React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { Rating } from 'elc-rating';
import { ProductRatingViewController } from '../../internal/controllers/ProductRatingViewController';
import { translate } from 'elc-service';
import { PRODUCT_RATING } from '../../internal/constants/DataTestIdAttributes';
import { classes } from '../../internal/utils/Classes';

export interface ITranslation {
    readReviewsLabel: string;
    firstToWriteReviewLabel: string;
}

export interface IProductRatingConfig {
    productId: string;
    hideReviewsNumber?: boolean;
    hideReadReviewsLink?: boolean;
    className?: string;
    isDisabled: boolean;
    numberOfStars: number;
    hideStars?: boolean;
    hideReviewsLabel?: boolean;
    percentageRating?: number;
    selectedRating?: number;
    productLink?: string;
    onReadReviewsClick?(): void;
}

export interface IProductRating {
    translations?: ITranslation;
    config: IProductRatingConfig;
    viewController: ProductRatingViewController;
}

const RatingWrapper = styled.div`
    width: 100%;
    justify-content: flex-end;
    display: flex;
`;

const ratingWrapperClassnames = classes('product-rating-wrapper');

@translate(['readReviewsLabel', 'firstToWriteReviewLabel'])
@observer
export class ProductRatingWrapper extends React.Component<IProductRating> {
    public static displayName: string;

    constructor(props: IProductRating) {
        super(props);
        props.viewController.initialize(props);
    }

    private readReviewsClickHandler = async () => {
        if (this.props.config.onReadReviewsClick) {
            this.props.config.onReadReviewsClick();
        } else {
            this.props.viewController.onReadReviewsClick();
        }
    };

    public render() {
        const { isLoaded } = this.props.viewController.data;
        if (!isLoaded) {
            return null;
        }

        const { readReviewsLabel, firstToWriteReviewLabel } = this.props
            .translations as ITranslation;
        const {
            className,
            isDisabled,
            numberOfStars,
            hideStars,
            hideReviewsLabel,
            productLink,
            hideReviewsNumber,
            hideReadReviewsLink
        } = this.props.config;

        const { onSelectRating, data } = this.props.viewController;
        const {
            numberOfReviews,
            selectedRatingIndex,
            percentageRating,
            onWriteReviewClick,
            averageRating
        } = data;

        return percentageRating !== undefined ? (
            <RatingWrapper className={ratingWrapperClassnames} data-test-id={PRODUCT_RATING}>
                <Rating
                    className={className}
                    isDisabled={isDisabled}
                    numberOfStars={numberOfStars}
                    readReviewsLabel={readReviewsLabel}
                    firstToWriteReviewLabel={firstToWriteReviewLabel}
                    selectedRating={selectedRatingIndex}
                    showReviews={!hideReviewsNumber}
                    showReadReviews={!hideReadReviewsLink}
                    hideStars={hideStars}
                    hideReviewsLabel={hideReviewsLabel}
                    percentageRating={percentageRating}
                    reviewsNumber={numberOfReviews}
                    onRatingClick={onSelectRating}
                    onWriteReviewsClick={onWriteReviewClick}
                    onReadReviewsClick={this.readReviewsClickHandler}
                    productLink={productLink}
                    averageRating={averageRating}
                />
            </RatingWrapper>
        ) : null;
    }
}

ProductRatingWrapper.displayName = 'ProductRatingWrapper';

export default ProductRatingWrapper;
